import { ActivitySelection } from 'components/Form/ActivityTree/types';
import {
  Activity,
  ContractProduct,
  ErrorResponse,
  LocationTypeEnum,
  PosTypeEnum,
} from 'generated/models';
import { CampaignType } from 'types/campaign';
import { LocationConfig } from 'types/config';

export enum PaymentTypeCodeEnum {
  PSP = 'PSP',
  Terminal = 'TERMINAL',
  ScanR = 'SCAN_R',
  CashRegister = 'CASH_REGISTER',
  Payconiq = 'PAYCONIQ',
  Marketplace = 'MARKETPLACE',
  UberEat = 'UBEREAT',
  Delivroo = 'DELIVROO',
  Phenix = 'PHENIX',
  TakeAway = 'TAKE_AWAY',
}

// TODO: remove and use PaymentTypeCodeEnum ?
export enum PaymentOptionEnum {
  Marketplace = 'marketplace',
  Payconiq = 'payconiq',
  POS = 'pos',
  PSP = 'psp',
}

export interface TerminalProviders {
  providers: Array<{ value: string; label: string }>;
  tids: Record<string, string>;
}

export enum TerminalAcquirerMidTypeEnum {
  MANDATORY = 'mid_mandatory',
  NOT_DISPLAYED = 'mid_not_displayed',
  OPTIONAL = 'mid_optional',
}

export enum TerminalAcquirerTidTypeEnum {
  MANDATORY = 'tid_mandatory',
  NOT_DISPLAYED = 'tid_not_visible',
  OPTIONAL = 'tid_optional',
}

export interface TerminalAcquirer {
  value: string;
  label: string;

  name: string;
  instructions: string;
  mid: TerminalAcquirerMidTypeEnum;
  mid_help_text: string;
  mid_placeholder: string;
  mid_validation_rule: string;
  tid: TerminalAcquirerTidTypeEnum;
  tid_unique: boolean;
  add_additional_terminal: boolean;
  add_additional_psp: boolean;
}

// string literal type (TS 4.1+)
export type PaymentTypeState =
  | `${string}.${string}.${string}` // paymentType + productCode + locationType
  | `${null}.${string}.${string}` // any + productCode + locationType
  | `${null}.${null}.${string}`; // any + any + locationType

export type PaymentTypeDict = Record<string, boolean>;

export type IsEnabledFn = (
  paymentTypeCode: PaymentTypeCodeEnum | null,
  productCodes: string[] | null,
  locationType: LocationTypeEnum | '',
) => boolean;

export interface ExtraWizardProps<C = LocationConfig> {
  callCacheTerminals: Record<string, ErrorResponse | null>;
  isOnboarding?: boolean;
  hasSelfcare?: boolean;
  isSkippable?: boolean;
  isPaymentEnabled?: IsEnabledFn;
  activities?: Activity[];
  campaign?: CampaignType;
  contractProducts?: ContractProduct[];
  contractId: string;
  config: C;
  terminalAcquirers?: TerminalAcquirer[];
  terminalProviders?: TerminalProviders;
}

export enum StepStatusEnum {
  Active,
  NotActive,
  Validated,
}

export interface CzechPOSValues {
  country: 'cz';
  id: string;
  bankId: string;
  deviceType: PosTypeEnum;
  productIds: string[];
  terminalId: string;
  terminalCode: string;
  masterId: string;
  email: string;
  certificateEmail: string;
}

export interface BelgiumPOSValues {
  country: 'be';
  id: string;
  terminalId: string;
  terminalProvider: string | null;
}

export type POSValues = CzechPOSValues | BelgiumPOSValues;

export interface CzechOnboardingValues {
  country: 'cz';
  name: string;
  streetName: string;
  houseNumber: string;
  orientationNumber: string;
  city: string;
  postalCode: string;
  webSiteUrl: string;
  email: string;
  phoneNumber: string;
  secondaryPhoneNumber: string;
  wifi: boolean;
  activities: ActivitySelection[];
  products: string[];
  merchantLocationId: string;
  POS: CzechPOSValues[];
}

export interface BelgiumOnboardingValues {
  country: 'be';
  type: LocationTypeEnum | '';
  sameCompanyInfo: boolean;

  locationName: string;

  // physical
  streetName: string;
  houseNumber: string;
  box: string;
  city: string;
  postalCode: string;
  // - takeover
  terminalTakeover: boolean;
  terminalCompanyId: string;
  terminalDelayDate: string | Date | null;
  terminalAcquirer: string;
  terminalMerchantId: string;
  POS: BelgiumPOSValues[];

  // webshop
  website: string;
  email: string;
  terminalAcquirerOnline: string;
  terminalMerchantIdOnline: string;
  POSOnline: BelgiumPOSValues[];

  phoneNumber: string;
  typeOfActivity: string;
  products: string[];

  paymentOptions: PaymentOptionEnum[];

  accountNumber: string;
}

export interface BelgiumValidationContext {
  isPaymentOptionEnabled: (code: PaymentTypeCodeEnum) => boolean;
  isProductEnabled: (productCode: string) => boolean;
  isTIDValid: (terminalId: string) => boolean;
  terminalAcquirers: TerminalAcquirer[];
  terminalAcquirer: string;
  terminalAcquirerOnline: string;
  terminalIds: Record<string, boolean>;
}

export interface CzechValidationContext {
  hasAllowedPosTypes: boolean;
}

// TODO: maybe use Discriminated Unions?
export type OnboardingValues = CzechOnboardingValues | BelgiumOnboardingValues;
